import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Button, SparkIcon, useEscapeKey } from '@spark';
import { EditColumnsDropdown } from '@/features/datasets/components/DataSelection/EditColumnsDropdown';

export const ExtraParamsColumnManager = ({
    availableExtraParamsKeys = [],
    currentExtraParamsTitles,
    onExtraParamsTitlesChange,
    disabled = false,
    taskType = null,
    onOpenChange,
}) => {
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [tempSelectedColumns, setTempSelectedColumns] = useState([]);
    const wrapperRef = useRef(null);

    const detectedKeys = useMemo(() => {
        const reservedFields = ['is_validated', 'is_document_reviewed'];
        return (availableExtraParamsKeys || []).filter(key => !reservedFields.includes(key));
    }, [availableExtraParamsKeys]);

    const closeDropdown = useCallback(() => setDropdownVisible(false), []);
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    const openDropdown = () => {
        if (disabled) return;
        if (dropdownVisible) {
            closeDropdown();
            return;
        }
        setTempSelectedColumns([...currentExtraParamsTitles]);
        setDropdownVisible(true);
    };

    const handleColumnChange = (key, checked) => {
        setTempSelectedColumns((prev) => (
            checked ? [...prev, key] : prev.filter((col) => col !== key)
        ));
    };

    const handleOk = () => {
        onExtraParamsTitlesChange(tempSelectedColumns);
        closeDropdown();
    };

    const handleReset = () => setTempSelectedColumns([]);

    useEscapeKey(closeDropdown, dropdownVisible);

    useEffect(() => {
        onOpenChangeRef.current?.(dropdownVisible);
        return () => {
            if (dropdownVisible) onOpenChangeRef.current?.(false);
        };
    }, [dropdownVisible]);

    // Close on outside click
    useEffect(() => {
        if (!dropdownVisible) return undefined;
        const onOutsideClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                closeDropdown();
            }
        };
        document.addEventListener('mousedown', onOutsideClick);
        return () => document.removeEventListener('mousedown', onOutsideClick);
    }, [dropdownVisible, closeDropdown]);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
            <Button
                variant="tertiary"
                size="small"
                type="button"
                disabled={disabled}
                icon={<SparkIcon slug="edit" size="small" />}
                className="edit-columns-button"
                onClick={openDropdown}
            >
                Edit columns
            </Button>

            {dropdownVisible && (
                <div className="extra-params-dropdown-panel">
                    <EditColumnsDropdown
                        availableExtraParamsKeys={detectedKeys}
                        tempSelectedColumns={tempSelectedColumns}
                        onDetectedColumnChange={handleColumnChange}
                        onOptuneColumnChange={handleColumnChange}
                        onReset={handleReset}
                        onOk={handleOk}
                        taskType={taskType}
                    />
                </div>
            )}
        </div>
    );
};
